import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  BatteryCharging,
  Wrench,
  Rocket,
  Building2,
  Check,
  Phone,
  Zap,
  Sun,
  Cpu,
} from 'lucide-react';
import CertificationsSection from '../../components/Certifications';
import { useT } from '../../i18n/LanguageContext';

const heroImg =
  'https://images.unsplash.com/photo-1593941707882-a5bba14938c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxFViUyMGNoYXJnaW5nfGVufDB8fHx8MTc4NDgxNjg0Mnww&ixlib=rb-4.1.0&q=85';

const content = {
  fr: {
    back: 'Tous nos services',
    badge: 'SERVICE · MOBILITÉ ÉLECTRIQUE',
    title: 'Mobilité',
    accent: 'électrique.',
    hero:
      'Conception et déploiement d’infrastructures de recharge pour véhicules électriques — du raccordement réseau à l’installation de bornes AC et DC.',
    home: 'Accueil',
    services: 'Services',
    current: 'Mobilité électrique',

    expertise: 'NOTRE EXPERTISE',
    expertiseTitle: 'Ce que nous réalisons pour vous',

    description:
      'GPE propose une solution clé en main adaptée à chaque besoin : que vous soyez propriétaire d’une maison individuelle, gestionnaire d’un logement collectif ou responsable d’une entreprise. Nos prestations couvrent l’ensemble de l’infrastructure électrique, du raccordement réseau à l’installation de bornes AC et DC. Nos solutions, conformes aux normes en vigueur, garantissent sécurité, performance et évolutivité afin d’accompagner durablement la mobilité électrique.',

    methodologyTitle: 'Méthodologie & accompagnement',
    methodology: [
      'Études de conception et d’exécution',
      'Modélisation et dimensionnement',
      'Accompagnement, assistance et conseils personnalisés',
      'Installation, maintenance et dépannage',
    ],

    solutionsTitle: 'Nos solutions',
    solutions: [
      'Bornes de recharge résidentielles (wallbox)',
      'Bornes collectives pour copropriétés et parkings',
      'Bornes DC rapides et ultra-rapides',
      'Infrastructure IRVE pour flottes d’entreprise',
      'Pré-équipement IRVE pour bâtiments neufs',
      'Supervision et télégestion des points de charge',
      'Recharge pour bus urbains et régionaux',
      'Recharge pour camions et véhicules lourds',
    ],

    sectorsTitle: 'Secteurs d’intervention',
    sectors: [
      'Logements / Habitat',
      'Tertiaire / Logistique / Bureaux',
      'Industriel',
      'Collectivités / Mairies',
    ],

    ctaTitle: 'Un projet mobilité électrique ?',
    ctaText:
      'Obtenez une étude personnalisée et un devis gratuit sous 24h.',
    quote: 'Demander un devis',
    call: 'Nous appeler',

    relatedTitle: 'Services complémentaires',
    related: [
      {
        slug: 'courants-forts',
        title: 'Courants forts',
        desc: 'Installations électriques BT/HT tertiaires et industrielles.',
        icon: Zap,
      },
      {
        slug: 'energies-renouvelables',
        title: 'Énergies renouvelables',
        desc: 'Solutions photovoltaïques clé en main.',
        icon: Sun,
      },
      {
        slug: 'courants-faibles',
        title: 'Courants faibles',
        desc: 'Vidéosurveillance, VDI, contrôle d’accès.',
        icon: Cpu,
      },
    ],
  },

  en: {
    back: 'All our services',
    badge: 'SERVICE · ELECTRIC MOBILITY',
    title: 'Electric',
    accent: 'mobility.',
    hero:
      'Design and deployment of electric vehicle charging infrastructure — from grid connection to AC and DC charging stations.',
    home: 'Home',
    services: 'Services',
    current: 'Electric mobility',

    expertise: 'OUR EXPERTISE',
    expertiseTitle: 'What we deliver for you',

    description:
      'GPE provides turnkey solutions adapted to every need, whether you are a homeowner, a residential property manager or a business owner. Our services cover the complete electrical infrastructure, from grid connection to AC and DC charging station installation. Our solutions comply with current standards and are designed to provide safety, performance and scalability while supporting the long-term development of electric mobility.',

    methodologyTitle: 'Methodology & support',
    methodology: [
      'Design and execution studies',
      'Modelling and sizing',
      'Personalised support, assistance and consulting',
      'Installation, maintenance and troubleshooting',
    ],

    solutionsTitle: 'Our solutions',
    solutions: [
      'Residential charging stations (wallbox)',
      'Shared charging stations for residential developments and car parks',
      'Fast and ultra-fast DC charging stations',
      'EV charging infrastructure for corporate fleets',
      'EV charging pre-equipment for new buildings',
      'Charging point monitoring and remote management',
      'Charging solutions for urban and regional buses',
      'Charging solutions for trucks and heavy-duty vehicles',
    ],

    sectorsTitle: 'Industries we serve',
    sectors: [
      'Residential / Housing',
      'Commercial / Logistics / Offices',
      'Industrial',
      'Public authorities / Municipalities',
    ],

    ctaTitle: 'Have an electric mobility project?',
    ctaText:
      'Get a personalised study and a free quotation within 24 hours.',
    quote: 'Request a quotation',
    call: 'Call us',

    relatedTitle: 'Related services',
    related: [
      {
        slug: 'courants-forts',
        title: 'Power systems',
        desc: 'Commercial and industrial LV/MV electrical installations.',
        icon: Zap,
      },
      {
        slug: 'energies-renouvelables',
        title: 'Renewable energy',
        desc: 'Turnkey photovoltaic solutions.',
        icon: Sun,
      },
      {
        slug: 'courants-faibles',
        title: 'Low-voltage systems',
        desc: 'Video surveillance, VDI and access control.',
        icon: Cpu,
      },
    ],
  },

  ar: {
    back: 'جميع خدماتنا',
    badge: 'الخدمة · التنقل الكهربائي',
    title: 'التنقل',
    accent: 'الكهربائي.',
    hero:
      'تصميم وتنفيذ البنية التحتية لشحن السيارات الكهربائية، من ربط الشبكة الكهربائية إلى تركيب محطات الشحن بالتيار المتردد والمستمر.',
    home: 'الرئيسية',
    services: 'الخدمات',
    current: 'التنقل الكهربائي',

    expertise: 'خبرتنا',
    expertiseTitle: 'ما نقدمه لكم',

    description:
      'تقدم GPE حلولاً متكاملة ومخصصة لكل احتياج، سواء كنتم مالكي منازل أو مسؤولي مجمعات سكنية أو مديري شركات. تشمل خدماتنا كامل البنية التحتية الكهربائية، بدءاً من ربط الشبكة وحتى تركيب محطات الشحن AC وDC. حلولنا متوافقة مع المعايير المعمول بها وتضمن السلامة والأداء وقابلية التطوير لدعم مستقبل التنقل الكهربائي.',

    methodologyTitle: 'المنهجية والمرافقة',
    methodology: [
      'دراسات التصميم والتنفيذ',
      'النمذجة والحسابات الفنية',
      'المرافقة والمساعدة والاستشارات المخصصة',
      'التركيب والصيانة وإصلاح الأعطال',
    ],

    solutionsTitle: 'حلولنا',
    solutions: [
      'محطات الشحن المنزلية Wallbox',
      'محطات الشحن المشتركة للمجمعات السكنية ومواقف السيارات',
      'محطات الشحن السريع وفائق السرعة DC',
      'البنية التحتية لشحن أساطيل الشركات',
      'التجهيز المسبق للبنية التحتية للشحن في المباني الجديدة',
      'المراقبة والإدارة عن بُعد لنقاط الشحن',
      'حلول شحن الحافلات داخل المدن وبين المناطق',
      'حلول شحن الشاحنات والمركبات الثقيلة',
    ],

    sectorsTitle: 'قطاعات التدخل',
    sectors: [
      'السكن / المباني السكنية',
      'القطاع التجاري / الخدمات اللوجستية / المكاتب',
      'القطاع الصناعي',
      'الهيئات المحلية / البلديات',
    ],

    ctaTitle: 'هل لديكم مشروع للتنقل الكهربائي؟',
    ctaText:
      'احصلوا على دراسة مخصصة وعرض سعر مجاني خلال 24 ساعة.',
    quote: 'طلب عرض سعر',
    call: 'اتصل بنا',

    relatedTitle: 'الخدمات ذات الصلة',
    related: [
      {
        slug: 'courants-forts',
        title: 'التيارات القوية',
        desc: 'التركيبات الكهربائية للمباني التجارية والصناعية.',
        icon: Zap,
      },
      {
        slug: 'energies-renouvelables',
        title: 'الطاقات المتجددة',
        desc: 'حلول الطاقة الشمسية المتكاملة.',
        icon: Sun,
      },
      {
        slug: 'courants-faibles',
        title: 'التيارات الضعيفة',
        desc: 'المراقبة بالفيديو وشبكات VDI والتحكم في الدخول.',
        icon: Cpu,
      },
    ],
  },
};

const CheckItem = ({ children }) => (
  <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-[#0b3b8c]/30 hover:shadow-sm transition-all">
    <span className="mt-0.5 w-5 h-5 rounded-full bg-[#0b3b8c]/10 text-[#0b3b8c] flex items-center justify-center shrink-0">
      <Check className="w-3 h-3" strokeWidth={3.5} />
    </span>

    <span className="text-slate-700 text-sm">
      {children}
    </span>
  </div>
);

const SectionTitle = ({ Icon, children }) => (
  <div className="flex items-center gap-3">
    <span className="w-9 h-9 rounded-lg bg-[#0b3b8c]/10 text-[#0b3b8c] flex items-center justify-center">
      <Icon className="w-4 h-4" strokeWidth={2.4} />
    </span>

    <h3 className="text-[#0b1c3a] font-bold text-lg md:text-xl tracking-tight">
      {children}
    </h3>
  </div>
);

const MobiliteElectrique = () => {
  const { lang } = useT();

  const currentLang = ['fr', 'en', 'ar'].includes(lang)
    ? lang
    : 'fr';

  const c = content[currentLang];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main
      className="bg-white"
      dir={currentLang === 'ar' ? 'rtl' : 'ltr'}
    >

      {/* HERO */}
      <section className="relative pt-40 pb-24 md:pt-48 md:pb-28 overflow-hidden">

        <img
          src={heroImg}
          alt={c.current}
          loading="eager"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-gradient-to-b from-[#0b1c3a]/95 via-[#0b1c3a]/80 to-[#0b1c3a]/95" />

        <div className="absolute right-[-10%] top-1/2 -translate-y-1/2 w-[720px] h-[720px] rounded-full bg-[#0b3b8c] blur-[140px] opacity-40 pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">

          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            {c.back}
          </Link>

          <div className="mt-8 flex items-center gap-4">

            <div className="w-12 h-12 rounded-xl bg-[#0b3b8c] flex items-center justify-center border border-white/10">
              <BatteryCharging
                className="w-6 h-6 text-white"
                strokeWidth={2.4}
              />
            </div>

            <span className="px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white/90 text-[11px] tracking-[0.25em] font-semibold">
              {c.badge}
            </span>

          </div>

          <h1 className="mt-6 text-white font-black leading-[1.05] tracking-tight text-4xl md:text-6xl lg:text-7xl max-w-4xl">

            {c.title}{' '}

            <span className="italic font-serif text-[#f5c518]">
              {c.accent}
            </span>

          </h1>

          <p className="mt-6 text-white/85 text-base md:text-lg max-w-2xl leading-relaxed">
            {c.hero}
          </p>

          <nav
            aria-label="breadcrumb"
            className="mt-8 flex items-center gap-2 text-white/70 text-sm"
          >

            <Link
              to="/"
              className="hover:text-white transition-colors"
            >
              {c.home}
            </Link>

            <span>/</span>

            <Link
              to="/services"
              className="hover:text-white transition-colors"
            >
              {c.services}
            </Link>

            <span>/</span>

            <span className="text-[#f5c518] font-semibold">
              {c.current}
            </span>

          </nav>

        </div>

        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-white" />

      </section>

      {/* MAIN */}
      <section className="pb-24">

        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1fr_360px] gap-10 lg:gap-14 items-start">

          {/* CONTENT */}
          <div>

            <span className="inline-block px-3 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-600 text-[11px] tracking-[0.25em] font-semibold">
              {c.expertise}
            </span>

            <h2 className="mt-5 text-3xl md:text-4xl font-black text-[#0b1c3a] leading-tight tracking-tight">
              {c.expertiseTitle}
            </h2>

            <div className="mt-6 p-5 md:p-6 rounded-2xl bg-slate-50 border-l-4 border-[#0b3b8c] text-slate-700 leading-relaxed">
              {c.description}
            </div>

            {/* METHODOLOGY */}
            <div className="mt-10">

              <SectionTitle Icon={Wrench}>
                {c.methodologyTitle}
              </SectionTitle>

              <div className="mt-5 grid sm:grid-cols-2 gap-3">

                {c.methodology.map((item) => (
                  <CheckItem key={item}>
                    {item}
                  </CheckItem>
                ))}

              </div>

            </div>

            {/* SOLUTIONS */}
            <div className="mt-12">

              <SectionTitle Icon={Rocket}>
                {c.solutionsTitle}
              </SectionTitle>

              <div className="mt-5 grid sm:grid-cols-2 gap-3">

                {c.solutions.map((item) => (
                  <CheckItem key={item}>
                    {item}
                  </CheckItem>
                ))}

              </div>

            </div>

            {/* SECTORS */}
            <div className="mt-12">

              <SectionTitle Icon={Building2}>
                {c.sectorsTitle}
              </SectionTitle>

              <div className="mt-5 grid sm:grid-cols-2 gap-3">

                {c.sectors.map((item) => (
                  <CheckItem key={item}>
                    {item}
                  </CheckItem>
                ))}

              </div>

            </div>

          </div>

          {/* CTA */}
          <aside className="lg:sticky lg:top-28">

            <div className="relative bg-[#0b1c3a] text-white rounded-3xl p-7 shadow-xl overflow-hidden">

              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#0b3b8c] blur-[70px] opacity-60" />

              <div className="relative">

                <div className="w-11 h-11 rounded-xl bg-[#f5c518] text-[#0b1c3a] flex items-center justify-center">
                  <BatteryCharging
                    className="w-5 h-5"
                    strokeWidth={2.4}
                  />
                </div>

                <h3 className="mt-5 text-xl font-bold leading-tight">
                  {c.ctaTitle}
                </h3>

                <p className="mt-3 text-white/70 text-sm leading-relaxed">
                  {c.ctaText}
                </p>

                <Link
                  to="/contact"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#0b3b8c] hover:bg-[#082e6f] font-semibold text-sm transition-colors"
                >
                  {c.quote}
                  <ArrowRight className="w-4 h-4" />
                </Link>

                <a
                  href="tel:+33442072262"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white text-[#0b1c3a] hover:bg-slate-100 font-semibold text-sm transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  {c.call}
                </a>

              </div>

            </div>

          </aside>

        </div>

      </section>

      {/* CERTIFICATIONS */}
      <CertificationsSection />

      {/* RELATED SERVICES */}
      <section className="py-20 bg-[#f7f8fb] border-t border-slate-200">

        <div className="max-w-7xl mx-auto px-6 lg:px-10">

          <h2 className="text-2xl md:text-3xl font-black text-[#0b1c3a] tracking-tight">
            {c.relatedTitle}
          </h2>

          <div className="mt-8 grid md:grid-cols-3 gap-4">

            {c.related.map((r) => {

              const Icon = r.icon;

              return (
                <Link
                  key={r.slug}
                  to={`/services/${r.slug}`}
                  className="group bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4 hover:shadow-md hover:border-[#0b3b8c]/30 transition-all"
                >

                  <div className="shrink-0 w-11 h-11 rounded-xl bg-[#0b3b8c]/10 text-[#0b3b8c] flex items-center justify-center group-hover:bg-[#0b3b8c] group-hover:text-white transition-colors">

                    <Icon
                      className="w-5 h-5"
                      strokeWidth={2.2}
                    />

                  </div>

                  <div className="min-w-0 flex-1">

                    <div className="text-[#0b1c3a] font-bold text-sm">
                      {r.title}
                    </div>

                    <div className="text-slate-500 text-xs mt-1 line-clamp-1">
                      {r.desc}
                    </div>

                  </div>

                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#0b3b8c] transition-colors" />

                </Link>
              );

            })}

          </div>

        </div>

      </section>

    </main>
  );
};

export default MobiliteElectrique;